
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types";
import { AssessmentResponse } from "@/types/assessment";
import { createServiceClient } from "@/services/migration/serviceClient";

export async function getResponsesByAssessmentId(assessmentId: string) {
  try {
    const { data, error } = await supabase
      .from("responses")
      .select("*")
      .eq("assessment_id", assessmentId);

    if (error) {
      console.error("Error fetching responses:", error);
      throw error;
    }

    // Transform database responses to AssessmentResponse objects
    return data.map(rawResponse => {
      // Parse JSON fields
      const personalInfo = typeof rawResponse.personal_info === 'string' 
        ? JSON.parse(rawResponse.personal_info as string) 
        : rawResponse.personal_info;
      
      const scoresByCategory = typeof rawResponse.scores_by_category === 'string'
        ? JSON.parse(rawResponse.scores_by_category as string)
        : rawResponse.scores_by_category;
      
      const maturityScores = typeof rawResponse.maturity_scores === 'string'
        ? JSON.parse(rawResponse.maturity_scores as string)
        : rawResponse.maturity_scores;
      
      const answerDetails = typeof rawResponse.answer_details === 'string'
        ? JSON.parse(rawResponse.answer_details as string)
        : rawResponse.answer_details;

      // Debug logging to help identify any data parsing issues
      console.log(`Response ${rawResponse.id} parsed data:`, {
        scoresByCategory,
        maturityScores
      });
      
      // Extract responses from answer_details for backwards compatibility
      const responseData = Object.entries(answerDetails || {}).map(([questionId, details]: [string, any]) => ({
        questionId,
        current: details.current?.value || details.value || 0,
        target: details.target?.value || 4
      }));
      
      // Map to AssessmentResponse format
      return {
        id: rawResponse.id,
        assessmentId: rawResponse.assessment_id,
        assessmentVersion: rawResponse.assessment_version,
        responses: responseData,
        personalInfo: {
          fullName: personalInfo?.name || personalInfo?.fullName || '',
          email: personalInfo?.email || '',
          company: personalInfo?.company || '',
          designation: personalInfo?.designation || '',
          consentGiven: personalInfo?.consentGiven || false
        },
        scoresByCategory: scoresByCategory || {},
        submittedAt: rawResponse.submitted_at,
        maturityScores: maturityScores || {},
        answerDetails: answerDetails || {}
      } as AssessmentResponse;
    });
  } catch (error) {
    console.error("Error in getResponses:", error);
    throw error;
  }
}

// Alias for backward compatibility
export const getAssessmentResponses = getResponsesByAssessmentId;

export async function createResponse(responseData: any) {
  // Prepare payload first so both clients use the same data
  const preparedData = await (async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return {
        assessment_id: responseData.assessment_id,
        assessment_version: responseData.assessment_version,
        user_id: user?.id || null,
        personal_info: responseData.personal_info as Json,
        scores_by_category: responseData.scores_by_category as Json,
        maturity_scores: responseData.maturity_scores as Json,
        answer_details: responseData.answer_details as Json
      };
    } catch {
      // If auth fails for any reason, fall back to null user_id
      return {
        assessment_id: responseData.assessment_id,
        assessment_version: responseData.assessment_version,
        user_id: null,
        personal_info: responseData.personal_info as Json,
        scores_by_category: responseData.scores_by_category as Json,
        maturity_scores: responseData.maturity_scores as Json,
        answer_details: responseData.answer_details as Json
      };
    }
  })();

  // Log what we're actually sending to the database
  console.log('Sending to database:', JSON.stringify(preparedData, null, 2));

  // 1) Try with the public client first (RLS should allow anon inserts)
  const insertWithClient = async () => {
    return await supabase
      .from("responses")
      .insert(preparedData)
      .select()
      .single();
  };

  // 2) Fallback to service role client if the first attempt fails (bypass RLS)
  const insertWithServiceRole = async () => {
    const admin = createServiceClient();
    return await admin
      .from("responses")
      .insert(preparedData)
      .select()
      .single();
  };

  try {
    let { data, error } = await insertWithClient();
    if (error) {
      console.warn('[createResponse] Public insert failed, retrying with service role:', error?.message || error);
      ({ data, error } = await insertWithServiceRole());
      if (error) {
        console.error('[createResponse] Service role insert also failed:', error);
        throw error;
      }
    }
    return data;
  } catch (error) {
    console.error("Error in createResponse:", error);
    throw error;
  }
}

export async function getResponseById(responseId: string) {
  try {
    const { data, error } = await supabase
      .from("responses")
      .select("*")
      .eq("id", responseId)
      .single();

    if (error) {
      console.error("Error fetching response:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error in getResponseById:", error);
    throw error;
  }
}

